import React, { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useColorModeValue,
  Icon,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { MdCloudUpload, MdPictureAsPdf, MdClose } from 'react-icons/md';
import { useBookById } from '../hooks/useBookById';
import { useUpdateBook } from '../hooks/useUpdateBook';
import { useAddBook } from '../hooks/useAddBook';

function BookForm({ editId, onSuccess, setSelectedId }) {
  const id = editId?._id;
  const [selectedFile, setSelectedFile] = useState(null);
  const [isExistingFileRemoved, setIsExistingFileRemoved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  const fileValue = watch('file');

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setValue('file', file, { shouldValidate: true });
        setIsExistingFileRemoved(false);
      }
    },
    [setValue],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setValue('file', null, { shouldValidate: id ? false : true });
    setIsExistingFileRemoved(true);
  };

  const { data, isLoading: isIdLoading } = useBookById(id);

  useEffect(() => {
    if (data?.data?.result) {
      reset({
        name: data.data.result.name,
      });
      // Reset file states when new data arrives
      setSelectedFile(null);
      setIsExistingFileRemoved(false);
    }
  }, [data, reset]);

  const createMutation = useAddBook({
    onSuccess: () => {
      reset();
      setSelectedFile(null);
      setIsExistingFileRemoved(false);
      onSuccess?.();
    },
  });
  const updateMutation = useUpdateBook({
    onSuccess: () => {
      reset();
      setSelectedFile(null);
      setIsExistingFileRemoved(false);
      setSelectedId(null);
      onSuccess?.();
    },
  });

  const submitHandler = (formData) => {
    const dataToSend = new FormData();
    const dataObj = { name: formData.name };
    if (id) dataObj.id = id;

    dataToSend.append('data', JSON.stringify(dataObj));

    if (formData.file) {
      dataToSend.append('attachment', formData.file);
    }

    if (id) {
      updateMutation.mutate(dataToSend);
      return;
    }
    createMutation.mutate(dataToSend);
  };

  const isLoading =
    createMutation.isPending || updateMutation.isPending || isIdLoading;

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue(
    'rgba(0, 0, 0, 0.1) 0px 4px 12px',
    'rgba(0, 0, 0, 0.4) 0px 4px 12px',
  );
  const dropzoneBg = useColorModeValue('gray.50', 'gray.700');
  const dropzoneBorder = useColorModeValue('gray.300', 'gray.600');
  const activeBorder = useColorModeValue('blue.500', 'blue.400');

  const showFilePreview = selectedFile || (!isExistingFileRemoved && data?.data?.result?.pdf);
  const fileNameToShow = selectedFile ? selectedFile.name : data?.data?.result?.pdf;

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card
        w="100%"
        maxW="600px"
        rounded="2xl"
        bg={cardBg}
        boxShadow={cardShadow}
        borderWidth="1px"
        borderColor={useColorModeValue('gray.100', 'gray.700')}
      >
        <CardBody p={8}>
          <form onSubmit={handleSubmit(submitHandler)}>
            <VStack spacing={6} align="stretch">
              <FormControl isInvalid={!!errors.name}>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.600">
                  Book Name{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  variant="filled"
                  placeholder="Enter book name (e.g. Electropathy Materia Medica)"
                  {...register('name', {
                    required: 'Book name is required',
                  })}
                  h="50px"
                  borderRadius="xl"
                />
                {errors.name && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {errors.name.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.file}>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.600">
                  Upload PDF{' '}
                  {!id && (
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  )}
                </FormLabel>

                <Box
                  {...getRootProps()}
                  p={6}
                  border="2px"
                  borderStyle="dashed"
                  borderColor={isDragActive ? activeBorder : dropzoneBorder}
                  rounded="xl"
                  bg={dropzoneBg}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: activeBorder,
                    bg: useColorModeValue('gray.100', 'gray.650'),
                  }}
                  textAlign="center"
                  position="relative"
                >
                  <input {...getInputProps()} />

                  {showFilePreview ? (
                    <HStack spacing={4} justify="center" p={2}>
                      <Icon as={MdPictureAsPdf} w={10} h={10} color="red.500" />
                      <VStack
                        align="start"
                        spacing={0}
                        flex={1}
                        overflow="hidden"
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="sm"
                          isTruncated
                          w="100%"
                        >
                          {fileNameToShow}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {selectedFile
                            ? `${(selectedFile.size / (1024 * 1024)).toFixed(
                                2,
                              )} MB`
                            : 'Existing file'}
                        </Text>
                      </VStack>
                      <IconButton
                        size="sm"
                        icon={<MdClose />}
                        colorScheme="red"
                        variant="ghost"
                        onClick={removeFile}
                        aria-label="Remove file"
                      />
                    </HStack>
                  ) : (
                    <VStack spacing={2}>
                      <Icon as={MdCloudUpload} w={12} h={12} color="blue.500" />
                      <Text fontWeight="bold" color="gray.700" fontSize="md">
                        {isDragActive
                          ? 'Drop it here!'
                          : 'Click or drag PDF to upload'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Only PDF files are accepted
                      </Text>
                    </VStack>
                  )}
                </Box>

                <input
                  type="hidden"
                  {...register('file', {
                    required: !id ? 'PDF file is required' : false,
                  })}
                />

                {errors.file && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {errors.file.message}
                  </Text>
                )}
              </FormControl>

              <Flex justify="flex-end" pt={4}>
                <Button
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  isLoading={isLoading}
                  px={10}
                  h="50px"
                  borderRadius="xl"
                  fontSize="md"
                  fontWeight="bold"
                  boxShadow="lg"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                  _active={{ transform: 'translateY(0)' }}
                >
                  {id ? 'Update' : 'Upload Book'}
                </Button>
              </Flex>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default BookForm;
